import { FilterOutlined } from '@ant-design/icons';
import { Table, Typography, Space, Button, Pagination, Radio, Row, Col, Popover, Input } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { ColumnsType } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { format } from 'date-fns';
import React from 'react';
import '@/GachaWinning/styles/gachaWinningTable.css';

import { GachaWinning } from '@/api-client/generated/models';

type GachaWinningUnapprovedTableProps = {
  gachaWinnings: GachaWinning[];
  gachaWinningsNum: number;
  currentPage: number;
  perPage: number;
  rowSelection: TableRowSelection<GachaWinning>;
  onChangeCurrentPage: (newPage: number) => Promise<void>;
  onChangeOs: (os: string) => Promise<void>;
  onApprove: (row: GachaWinning) => void;
  onReject: (row: GachaWinning) => void;
  onSearch: (word: string) => void;
};

type TableHeaderProps = {
  totalNum: number;
  onSearch: (word: string) => void;
};

type OsHeaderProps = {
  // eslint-disable-next-line react/require-default-props
  onChangeOsRadio?: (e: RadioChangeEvent) => void;
};

const dateFormat = 'yyyy/MM/dd HH:mm:ss';

const OsHeader: React.FC<OsHeaderProps> = ({ onChangeOsRadio }) => {
  const content = (
    <Radio.Group className="os-select" onChange={onChangeOsRadio} defaultValue="">
      <Radio value="" className="os-select-el">
        全てのOS
      </Radio>
      <Radio value="ios" className="os-select-el">
        iOS
      </Radio>
      <Radio value="android" className="os-select-el">
        Android
      </Radio>
    </Radio.Group>
  );
  return (
    <Space>
      OS
      <Popover content={content} trigger="focus" placement="bottom">
        <Button type="text">
          <FilterOutlined />
        </Button>
      </Popover>
    </Space>
  );
};

const TableHeader: React.FC<TableHeaderProps> = ({ totalNum, onSearch }) => (
  <Row>
    <Col span={2}>
      <Typography.Title level={5}>総数: {totalNum || 0}</Typography.Title>
    </Col>
    <Col span={6}>
      <Input.Search placeholder="フリーワード検索" onSearch={onSearch} />
    </Col>
  </Row>
);

const GachaWinningUnapprovedTable: React.FC<GachaWinningUnapprovedTableProps> = ({
  gachaWinnings,
  gachaWinningsNum,
  currentPage,
  perPage,
  rowSelection,
  onChangeCurrentPage,
  onChangeOs,
  onApprove,
  onReject,
  onSearch,
}) => {
  const actionButtons = (row: GachaWinning) => (
    <Space>
      <Button type="primary" onClick={() => onApprove(row)}>
        承認
      </Button>
      <Button danger onClick={() => onReject(row)}>
        棄却
      </Button>
    </Space>
  );

  const onChangeOsRadio = (e: RadioChangeEvent) => {
    onChangeOs(e.target.value);
  };

  const onChangeCurrentPagePagination = (page: number) => {
    onChangeCurrentPage(page);
  };

  const columns: ColumnsType = [
    {
      title: 'ユーザ名',
      dataIndex: ['user', 'nickname'],
      key: 'username',
    },
    {
      title: 'メールアドレス',
      dataIndex: ['user', 'mailAddress'],
      key: 'mailAddress',
    },
    {
      title: OsHeader({ onChangeOsRadio }),
      dataIndex: 'os',
      key: 'os',
    },
    {
      title: '当選日時',
      key: 'createdAt',
      render: (_: any, row: GachaWinning) => format(row.createdAt, dateFormat),
    },
    {
      title: 'ギフトコード',
      key: 'giftCode',
      dataIndex: ['giftCode', 'code'],
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, row: GachaWinning) => actionButtons(row),
    },
  ];

  return (
    <div>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={gachaWinnings}
        rowKey={(gachaWinning: GachaWinning) => gachaWinning.id}
        pagination={false}
        title={() => TableHeader({ totalNum: gachaWinningsNum, onSearch })}
      />
      <Pagination
        simple
        current={currentPage}
        pageSize={perPage}
        total={gachaWinningsNum}
        onChange={onChangeCurrentPagePagination}
      />
    </div>
  );
};

export default GachaWinningUnapprovedTable;
